"use client";

import { useCallback, useEffect, useState } from "react";

/**
 * Counts down from a number of seconds to zero, ticking once a second.
 *
 * Ticks are scheduled off the wall clock rather than by decrementing a
 * counter, so a tab that gets throttled or backgrounded resumes showing the
 * real remaining time instead of however far the interval happened to get.
 */
export const useCountdown = (
  initialSeconds: number
): { secondsLeft: number; restart: (seconds?: number) => void } => {
  const [deadline, setDeadline] = useState(
    () => Date.now() + initialSeconds * 1000
  );
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

  useEffect(() => {
    const sync = () =>
      setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));

    sync();
    const id = setInterval(sync, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  const restart = useCallback(
    (seconds: number = initialSeconds) => {
      setDeadline(Date.now() + seconds * 1000);
    },
    [initialSeconds]
  );

  return { secondsLeft, restart };
};

/** Formats a duration as m:ss, e.g. 92 -> "1:32". */
export const formatCountdown = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};
